import React, { useEffect, useState } from "react";
import { ShieldCheck, AlertTriangle, Monitor, ChevronLeft, ChevronRight } from "lucide-react";
import { apiGet } from "../../utils/api";
import Skeleton from "../common/Skeleton";
import "./analytics.css";
import "../devices/devices.css";

const ALERTS_PAGE_SIZE = 25;

const toYmd = (dateObj) => dateObj.toISOString().split("T")[0];

const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const getMonthRange = (monthValue) => {
    const match = /^(\d{4})-(\d{2})$/.exec(String(monthValue || ""));
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;

    const start = new Date(Date.UTC(year, month - 1, 1));
    const end = new Date(Date.UTC(year, month, 0));
    return { start: toYmd(start), end: toYmd(end) };
};

// Plain toLocaleString() renders a slash-separated numeric date (e.g. "9/7/2026")
// whose month/day order silently flips between locales — spelling out the month
// removes the ambiguity regardless of the viewer's locale.
const formatDateTime = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
        day: "2-digit", month: "short", year: "numeric",
        hour: "numeric", minute: "2-digit",
    });
};

export default function RiskCompliance() {
    const [securityData, setSecurityData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [threatTypeFilter, setThreatTypeFilter] = useState("all");
    const [alertsPage, setAlertsPage] = useState(1);

    useEffect(() => {
        let isMounted = true;

        const fetchSecurityPulse = async (showSkeleton = false) => {
            if (showSkeleton) setLoading(true);
            try {
                const range = getMonthRange(getCurrentMonth());
                const params = new URLSearchParams({ filter: "custom", start: range.start, end: range.end });
                const res = await apiGet(`/api/analytics/security-pulse?${params.toString()}`);
                const result = await res.json();
                if (!isMounted) return;
                if (result.success) setSecurityData(result);
            } catch (err) {
                console.error("Security Pulse Error:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchSecurityPulse(true);
        const interval = setInterval(() => fetchSecurityPulse(false), 30000);
        return () => { isMounted = false; clearInterval(interval); };
    }, []);

    const threatTypeOptions = React.useMemo(() => {
        const types = new Set((securityData?.threats || []).map(t => t.type).filter(Boolean));
        return Array.from(types).sort();
    }, [securityData?.threats]);

    const visibleThreats = React.useMemo(() => {
        return (securityData?.threats || []).filter((t) => {
            if (threatTypeFilter === "all") return true;
            return t.type === threatTypeFilter;
        });
    }, [securityData?.threats, threatTypeFilter]);

    // Land back on page 1 whenever the type filter (or the underlying threat
    // list) changes, so you never end up stuck on a now-empty trailing page.
    useEffect(() => { setAlertsPage(1); }, [threatTypeFilter, securityData?.threats]);

    const alertsTotalPages = Math.max(1, Math.ceil(visibleThreats.length / ALERTS_PAGE_SIZE));
    const pagedThreats = visibleThreats.slice((alertsPage - 1) * ALERTS_PAGE_SIZE, alertsPage * ALERTS_PAGE_SIZE);

    if (loading && !securityData) {
        return (
            <main className="zoho-main">
                <div className="zoho-body">
                    <div className="zoho-grid">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="zoho-card col-4">
                                <div className="card-body kpi-wrapper">
                                    <Skeleton type="text" width="60%" />
                                    <Skeleton type="title" height="26px" style={{ margin: '8px 0' }} />
                                    <Skeleton type="text" width="80%" />
                                </div>
                            </div>
                        ))}
                        <div className="zoho-card col-4">
                            <div className="card-header"><Skeleton type="text" width="150px" /></div>
                            <div className="card-body" style={{ height: '260px' }}><Skeleton type="card" height="100%" /></div>
                        </div>
                        <div className="zoho-card col-8">
                            <div className="card-header"><Skeleton type="text" width="150px" /></div>
                            <div className="card-body" style={{ height: '260px' }}><Skeleton type="card" height="100%" /></div>
                        </div>
                        <div className="zoho-card col-12">
                            <div className="card-header"><Skeleton type="text" width="180px" /></div>
                            <div className="card-body" style={{ height: '300px' }}><Skeleton type="card" height="100%" /></div>
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main className="zoho-main">
            <div className="zoho-body">
                <div className="zoho-grid">
                    {/* Security KPIs */}
                    <div className="zoho-card col-4">
                        <div className="card-body kpi-wrapper">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <ShieldCheck size={20} color={securityData?.summary?.healthScore < 70 ? '#ff5630' : '#36b37e'} />
                                <span className="kpi-subtext">SYSTEM SAFETY SCORE</span>
                            </div>
                            <div className="kpi-value" style={{ fontSize: 32, color: securityData?.summary?.healthScore < 70 ? '#ff5630' : '#36b37e' }}>
                                {securityData?.summary?.healthScore ?? 100}%
                            </div>
                            <div className="kpi-subtext">{securityData?.summary?.healthScore >= 80 ? 'Optimal health' : 'Review alerts urgently'}</div>
                        </div>
                    </div>
                    <div className="zoho-card col-4">
                        <div className="card-body kpi-wrapper">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <AlertTriangle size={20} color="#ffab00" />
                                <span className="kpi-subtext">ACTIVE THREATS</span>
                            </div>
                            <div className="kpi-value" style={{ fontSize: 32 }}>{securityData?.summary?.totalThreats || 0}</div>
                            <div className="kpi-subtext">Total alerts in selected period</div>
                        </div>
                    </div>
                    <div className="zoho-card col-4">
                        <div className="card-body kpi-wrapper">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                <Monitor size={20} color="#0052cc" />
                                <span className="kpi-subtext">ENDPOINTS MONITORED</span>
                            </div>
                            <div className="kpi-value" style={{ fontSize: 32 }}>{securityData?.summary?.monitoredEndpoints || 0}</div>
                            <div className="kpi-subtext">Devices secured globally</div>
                        </div>
                    </div>

                    {/* Alert Breakdown & Trend */}
                    <div className="zoho-card col-4">
                        <div className="card-header"><h3>Threat Categories</h3></div>
                        <div className="card-body"><ThreatBreakdownChart threats={securityData?.threats} /></div>
                    </div>
                    <div className="zoho-card col-8">
                        <div className="card-header"><h3>Risk Volume Trend</h3></div>
                        <div className="card-body" style={{ height: 260 }}><SecurityRiskChart data={securityData?.trend} /></div>
                    </div>

                    {/* Unified Threat Feed */}
                    <div className="zoho-card col-12">
                        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3>Unified Security Alert Feed</h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <select
                                    value={threatTypeFilter}
                                    onChange={e => setThreatTypeFilter(e.target.value)}
                                    style={{ border: '1px solid var(--zoho-border)', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, background: 'var(--zoho-surface, rgba(255,255,255,0.06))', color: 'var(--zoho-text-primary)', cursor: 'pointer', minWidth: 160, outline: 'none' }}
                                >
                                    <option value="all">All Types</option>
                                    {threatTypeOptions.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="card-body" style={{ padding: 0 }}>
                            <div className="zoho-table-wrap">
                            <table className="zoho-table">
                                <thead>
                                    <tr>
                                        <th>Severity</th>
                                        <th>Threat Type</th>
                                        <th>Device ID</th>
                                        <th>Event Details</th>
                                        <th>Time Detected</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedThreats.map((threat, i) => (
                                        <tr key={i}>
                                            <td>
                                                <span style={{ padding: '4px 10px', borderRadius: 4, fontSize: 11, fontWeight: 800, background: threat.severity === 'Critical' ? '#ffeceb' : threat.severity === 'High' ? '#fff7e6' : 'rgba(255,255,255,0.06)', color: threat.severity === 'Critical' ? '#ff5630' : threat.severity === 'High' ? '#ffab00' : 'var(--zoho-text-secondary)' }}>
                                                    {threat.severity.toUpperCase()}
                                                </span>
                                            </td>
                                            <td style={{ fontWeight: 600, color: 'var(--zoho-text-primary)' }}>{threat.type}</td>
                                            <td style={{ fontSize: 12 }}>{threat.agentId}</td>
                                            <td style={{ color: 'var(--zoho-text-secondary)', fontSize: 12 }}>{threat.description}</td>
                                            <td style={{ fontSize: 11 }}>{formatDateTime(threat.time)}</td>
                                        </tr>
                                    ))}
                                    {visibleThreats.length === 0 && (!securityData?.threats || securityData.threats.length === 0) && (
                                        <tr><td colSpan="5" style={{ textAlign: 'center', padding: 60, color: 'var(--zoho-text-secondary)' }}>
                                            <ShieldCheck size={48} color="#36b37e" style={{ marginBottom: 16, display: 'block', margin: '0 auto 16px' }} />
                                            <h3 style={{ margin: 0, color: 'var(--zoho-text-primary)' }}>No Security Threats Detected</h3>
                                            <p style={{ marginTop: 8 }}>Your organization's network and devices are currently secure.</p>
                                        </td></tr>
                                    )}
                                    {visibleThreats.length === 0 && securityData?.threats?.length > 0 && (
                                        <tr><td colSpan="5" style={{ textAlign: 'center', padding: 60, color: 'var(--zoho-text-secondary)' }}>
                                            No alerts matching "{threatTypeFilter}" for this period.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                            </div>
                        </div>
                        {visibleThreats.length > 0 && (
                            <div className="devices-pagination">
                                <span className="devices-pagination-count">
                                    {(alertsPage - 1) * ALERTS_PAGE_SIZE + 1}–{Math.min(alertsPage * ALERTS_PAGE_SIZE, visibleThreats.length)} of {visibleThreats.length} alert{visibleThreats.length !== 1 ? "s" : ""}
                                </span>
                                <div className="devices-pagination-controls">
                                    <button
                                        className="gc-icon-btn"
                                        data-tip="Previous page"
                                        onClick={() => setAlertsPage((p) => Math.max(1, p - 1))}
                                        disabled={alertsPage <= 1}
                                    >
                                        <ChevronLeft size={15} />
                                    </button>
                                    <span className="devices-pagination-page">{alertsPage} / {alertsTotalPages}</span>
                                    <button
                                        className="gc-icon-btn"
                                        data-tip="Next page"
                                        onClick={() => setAlertsPage((p) => Math.min(alertsTotalPages, p + 1))}
                                        disabled={alertsPage >= alertsTotalPages}
                                    >
                                        <ChevronRight size={15} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}

const SecurityRiskChart = ({ data }) => {
    const [hoverIndex, setHoverIndex] = useState(null);
    if (!data || data.length === 0) return <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--zoho-text-faint)' }}>No risk data trend available</div>;

    const height = 240;
    const width = 500;
    const padding = { top: 16, right: 16, bottom: 44, left: 16 };
    const innerWidth = Math.max(1, width - padding.left - padding.right);
    const innerHeight = Math.max(1, height - padding.top - padding.bottom);

    const maxVal = 100;
    const minVal = 0;

    const denom = Math.max(1, data.length - 1);
    const getX = (i) => padding.left + (i / denom) * innerWidth;
    const getY = (val) => (padding.top + innerHeight) - ((val - minVal) / (maxVal - minVal)) * innerHeight;

    let pathD = `M ${getX(0)} ${getY(data[0].risk)}`;
    data.forEach((d, i) => { pathD += ` L ${getX(i)} ${getY(d.risk)}`; });

    const baseY = padding.top + innerHeight;
    const areaD = `${pathD} L ${getX(data.length - 1)} ${baseY} L ${getX(0)} ${baseY} Z`;

    const tickCount = Math.min(6, data.length);
    const tickStep = tickCount <= 1 ? 1 : Math.ceil((data.length - 1) / (tickCount - 1));
    const tickIndexes = [];
    for (let i = 0; i < data.length; i += tickStep) tickIndexes.push(i);
    if (tickIndexes[tickIndexes.length - 1] !== data.length - 1) tickIndexes.push(data.length - 1);

    const yTicks = [0, 50, 100];

    const handleMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const relX = e.clientX - rect.left;
        const t = rect.width > 0 ? relX / rect.width : 0;
        const viewX = t * width;
        const idx = Math.min(
            data.length - 1,
            Math.max(0, Math.round(((viewX - padding.left) / innerWidth) * denom))
        );
        setHoverIndex(idx);
    };

    const activePoint = hoverIndex != null ? data[hoverIndex] : null;
    const activeX = hoverIndex != null ? getX(hoverIndex) : null;
    const activeY = hoverIndex != null ? getY(activePoint?.risk || 0) : null;
    const activeXPct = activeX != null ? (activeX / width) * 100 : 0;
    const activeYPct = activeY != null ? (activeY / height) * 100 : 0;

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            {activePoint && (
                <div
                    style={{
                        position: 'absolute',
                        left: `${activeXPct}%`,
                        top: `${activeYPct}%`,
                        transform: 'translate(-50%, -110%)',
                        background: 'rgba(9, 30, 66, 0.92)',
                        color: 'white',
                        padding: '6px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        pointerEvents: 'none',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <div style={{ fontWeight: 700 }}>{activePoint.label || `#${hoverIndex + 1}`}</div>
                    <div style={{ opacity: 0.9 }}>Risk level: {Math.round(activePoint.risk || 0)}/100</div>
                </div>
            )}

            <svg
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                style={{ width: '100%', height: '100%' }}
                onMouseMove={handleMove}
                onMouseLeave={() => setHoverIndex(null)}
            >
                <defs>
                    <linearGradient id="riskGradient" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#ff5630" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#ff5630" stopOpacity="0" />
                    </linearGradient>
                </defs>

                {yTicks.map((v, i) => (
                    <g key={i}>
                        <line x1={padding.left} y1={getY(v)} x2={width - padding.right} y2={getY(v)} stroke="rgba(255,255,255,0.10)" />
                        <text x={padding.left} y={getY(v) - 4} fontSize="10" fill="var(--zoho-text-muted)">{v}</text>
                    </g>
                ))}

                <line x1={padding.left} y1={baseY} x2={width - padding.right} y2={baseY} stroke="#ebecf0" />
                {tickIndexes.map((i) => (
                    <g key={i}>
                        <line x1={getX(i)} y1={baseY} x2={getX(i)} y2={baseY + 4} stroke="var(--zoho-text-faint)" />
                        <text x={getX(i)} y={height - 14} textAnchor="middle" fontSize="10" fill="var(--zoho-text-secondary)">
                            {data[i]?.label || ""}
                        </text>
                    </g>
                ))}

                <path d={areaD} fill="url(#riskGradient)" />
                <path d={pathD} fill="none" stroke="#ff5630" strokeWidth="2" strokeLinecap="round" />
                {data.length < 40 && data.map((d, i) => (
                    <circle key={i} cx={getX(i)} cy={getY(d.risk)} r={hoverIndex === i ? 4 : 3} fill={(d.risk || 0) > 70 ? "#ff5630" : "#ffab00"} opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.55} />
                ))}

                {hoverIndex != null && (
                    <line x1={getX(hoverIndex)} y1={padding.top} x2={getX(hoverIndex)} y2={baseY} stroke="#ff5630" strokeDasharray="3 3" opacity="0.35" />
                )}
            </svg>
        </div>
    );
};

const ThreatBreakdownChart = ({ threats }) => {
    const categories = [
        { key: 'Data Exfiltration', color: '#ff5630', label: 'File Uploads' },
        { key: 'File Manipulation', color: '#ffab00', label: 'File Renames' },
        { key: 'Unsafe Web Activity', color: '#00b8d9', label: 'Risky URLs' },
        { key: 'Unauthorized USB', color: '#36b37e', label: 'USB Events' },
        { key: 'Restricted App', color: '#6554c0', label: 'Shadow IT' },
        { key: 'Restricted Website', color: '#8993a4', label: 'Restricted Sites' },
        { key: 'System Anomaly', color: 'var(--zoho-text-muted)', label: 'Anomalies' },
        { key: 'Network Vulnerability', color: '#bf2600', label: 'Vuln Scan' },
    ];

    const counts = {};
    (threats || []).forEach(t => { counts[t.type] = (counts[t.type] || 0) + 1; });
    const total = Object.values(counts).reduce((s, v) => s + v, 0);

    if (total === 0) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <ShieldCheck size={36} color="#36b37e" />
                <div style={{ fontSize: 12, color: 'var(--zoho-text-secondary)', marginTop: 8 }}>No threats detected</div>
            </div>
        );
    }

    let cumPercent = 0;
    const segments = categories.map(cat => {
        const val = counts[cat.key] || 0;
        const pct = val / total;
        const seg = { ...cat, count: val, pct, startPct: cumPercent };
        cumPercent += pct;
        return seg;
    }).filter(s => s.count > 0);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ position: 'relative', width: 130, height: 130, margin: '0 auto' }}>
                <svg viewBox="0 0 40 40" style={{ width: '100%', height: '100%' }}>
                    {segments.map((seg, i) => {
                        const dash = `${seg.pct * 100} ${100 - seg.pct * 100}`;
                        const offset = 25 - seg.startPct * 100;
                        return (
                            <circle key={i} r="15.9155" cx="20" cy="20" fill="transparent"
                                stroke={seg.color} strokeWidth="5"
                                strokeDasharray={dash} strokeDashoffset={offset}
                                style={{ transformOrigin: 'center', transform: 'rotate(-90deg)', transition: 'stroke-dasharray 0.5s' }}
                            />
                        );
                    })}
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--zoho-text-primary)' }}>{total}</div>
                    <div style={{ fontSize: 9, color: 'var(--zoho-text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>Total</div>
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {segments.map((seg, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 8, height: 8, flexShrink: 0, borderRadius: 2, background: seg.color }}></span>
                        <span style={{ flex: 1, fontSize: 11, color: 'var(--zoho-text-secondary)' }}>{seg.label}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--zoho-text-primary)' }}>{seg.count}</span>
                        <span style={{ fontSize: 10, color: 'var(--zoho-text-muted)', minWidth: 28, textAlign: 'right' }}>{Math.round(seg.pct * 100)}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
